import { useDeferredValue, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

export type EngagementResourceSource = 'servicenow' | 'cloud' | 'none';

export interface EngagementResource {
  id: string;
  name: string;
  resourceId?: string;
  resourceGroupName?: string;
  resourceType?: string;
  cloudProvider?: string;
  status?: string;
  region?: string | null;
}

export interface EngagementResourcesResult {
  resources: EngagementResource[];
  source: EngagementResourceSource;
}

/**
 * Loads Resource dropdown options for an engagement.
 * When the engagement is the ServiceNow default engagement, returns CMDB
 * Configuration Items (as Pulse Resource stubs). Search is sent to ServiceNow
 * so it matches across the full CMDB (not only the initial page of results).
 *
 * `pinnedResource` keeps the currently selected item visible after search clears
 * (selected CI may not be in the default first page of results).
 */
export function useEngagementResources(
  engagementId: string | undefined | null,
  search?: string,
  pinnedResource?: EngagementResource | null
) {
  const deferredSearch = useDeferredValue((search || '').trim());

  const query = useQuery<EngagementResourcesResult>({
    queryKey: ['engagement-resources', engagementId, deferredSearch],
    queryFn: async () => {
      if (!engagementId) {
        return { resources: [], source: 'none' };
      }

      try {
        const params = new URLSearchParams();
        if (deferredSearch) {
          params.set('search', deferredSearch);
        }
        const qs = params.toString();
        const snRes = await api.get(
          `/integrations/servicenow/engagements/${engagementId}/configuration-items${
            qs ? `?${qs}` : ''
          }`
        );
        if (snRes.data?.enabled) {
          return {
            resources: Array.isArray(snRes.data.resources) ? snRes.data.resources : [],
            source: 'servicenow',
          };
        }
      } catch {
        // Fall back to cloud resources if ServiceNow CI fetch fails
      }

      const params = new URLSearchParams({ engagementId });
      if (deferredSearch) {
        params.set('search', deferredSearch);
      }
      const response = await api.get(`/resources?${params.toString()}`);
      const resources = Array.isArray(response.data)
        ? response.data
        : response.data?.resources || [];
      return {
        resources: Array.isArray(resources) ? resources : [],
        source: 'cloud',
      };
    },
    enabled: !!engagementId,
  });

  const resources = useMemo(() => {
    const list = query.data?.resources || [];
    if (!pinnedResource?.id) {
      return list;
    }
    if (list.some((r) => r.id === pinnedResource.id)) {
      return list;
    }
    return [pinnedResource, ...list];
  }, [query.data?.resources, pinnedResource]);

  return {
    ...query,
    data: query.data
      ? { ...query.data, resources }
      : pinnedResource?.id
        ? { resources: [pinnedResource], source: 'none' as EngagementResourceSource }
        : query.data,
  };
}
